"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: sidebarRef,
    isVisible: sidebarVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    number: "01",
    question: "How does the MedaCube system work?",
    answer: "The MedaCube is an automated medication dispenser that organizes your pills into individual doses. It eliminates the need for manual pill sorting, ensuring that the correct medication is dispensed at the precise time required, significantly reducing the risk of human error."
  }, {
    number: "02",
    question: "Can I monitor my loved one's medication adherence?",
    answer: "Yes. Our system provides real-time insights for caregivers. You will receive notifications if a dose is missed, allowing you to intervene promptly and ensure your loved one stays on their prescribed health regimen without constant physical presence."
  }, {
    number: "03",
    question: "Is the device difficult to set up?",
    answer: "We prioritize simplicity. Our team handles the initial configuration and setup process, ensuring the device is ready for use immediately. We provide clear instructions for caregivers to manage refills and settings with minimal effort."
  }, {
    number: "04",
    question: "What happens if there is a power outage?",
    answer: "The MedaCube is equipped with a reliable battery backup system. In the event of a power failure, the device continues to operate, ensuring that scheduled doses are still dispensed accurately and your peace of mind remains intact."
  }, {
    number: "05",
    question: "Is this service suitable for seniors living alone?",
    answer: "Absolutely. AthenaCare is designed specifically to support independent living. By automating medication management, we empower seniors to maintain their autonomy while providing caregivers with the oversight necessary to ensure safety and health compliance."
  }, {
    number: "06",
    question: "How do I get started with AthenaCare?",
    answer: "Getting started is simple. Contact our team through our inquiry form to schedule a consultation. We will discuss your specific needs, explain how our technology integrates into your home, and guide you through the onboarding process."
  }];
  return <section data-section-id="3289" id="common-questions" className="bg-background py-24 md:py-32 text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div ref={sidebarRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${sidebarVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs uppercase tracking-[0.25em] font-mono text-slate-text mb-8">
                Common Questions
              </p>
              <h2 className="font-default text-foreground leading-[1.05] mb-8" style={{
              fontSize: "clamp(2.5rem, 5vw, 3.5rem)"
            }}>
                Care,{" "}
                <span className="italic text-primary">simplified</span>.
              </h2>
              <p className="font-default text-lg leading-relaxed text-secondary mb-10">Clear answers to the most frequent questions about our medication management technology and how we support independence.</p>
              <div className="pt-8 border-t border-border">
                <p className="font-default text-base text-muted-foreground">
                  Still have questions?{" "}
                  <Link to="/Contact" className="italic text-foreground underline underline-offset-4 decoration-primary hover:text-primary transition-colors duration-300">
                    Contact our team
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8" ref={faqRef}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className={`group border-b border-border last:border-b-0 border-t-0 first:border-t border-border data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300 ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionProperty: "opacity, transform, padding, border-color",
              transitionDuration: "700ms",
              transitionDelay: faqVisible ? `${i * 80}ms` : "0ms",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }}>
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group/trigger">
                    <div className="flex items-start gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-wider text-slate-text pt-2 shrink-0 w-8">
                        {faq.number}
                      </span>
                      <span className="font-default text-foreground leading-snug flex-1" style={{
                    fontSize: "clamp(1.125rem, 2vw, 1.375rem)"
                  }}>
                        {faq.question}
                      </span>
                    </div>
                    <div className="relative w-5 h-5 shrink-0 mt-3 ml-4">
                      <span className="absolute top-1/2 left-0 w-5 h-px bg-foreground -translate-y-1/2"></span>
                      <span className="absolute top-1/2 left-0 w-5 h-px bg-foreground -translate-y-1/2 rotate-90 transition-transform duration-400 ease-out group-data-[state=open]:rotate-0"></span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <div className="flex gap-6">
                      <span className="font-mono text-xs shrink-0 w-8 opacity-0" aria-hidden="true">
                        {faq.number}
                      </span>
                      <p className="font-default leading-[1.7] text-secondary max-w-2xl" style={{
                    fontSize: "clamp(1rem, 1.4vw, 1.125rem)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}